import type { ReactElement } from "react";

import { useSuccessToast } from "app/pages/sales-intelligence/hooks/useSuccessToast";
import SignalsCreateViewContainer from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalsCreateView/SignalsCreateViewContainer";
import type { SignalsManagementProps } from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalsManagement/SignalsManagementContainer";
import SignalsManagementContainer from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalsManagement/SignalsManagementContainer";
import SignalsUpdateViewContainer from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalsUpdateView/SignalsUpdateViewContainer";
import type Signal from "app/pages/sales-intelligence/sub-modules/signals/models/core/Signal";
import type CreateViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-management/CreateViewProps";
import type EditViewProps from "app/pages/sales-intelligence/sub-modules/signals/models/view/signals-management/EditViewProps";
import signalsI18nKeys from "app/pages/sales-intelligence/sub-modules/signals/utils/i18n";

interface Props extends Omit<SignalsManagementProps, "createView" | "editView"> {
    onSignalCreateSuccess?: (signal: Signal) => void;
    onSignalUpdateSuccess?: (signal: Signal) => void;
}

const BaseSignalsManagementContainer = ({
    onSignalCreateSuccess,
    onSignalUpdateSuccess,
    ...signalsManagementProps
}: Props): ReactElement => {
    const { showSuccess } = useSuccessToast();

    function handleSignalDeleteSuccess(): void {
        showSuccess(signalsI18nKeys.notification.success.removeSignal);
    }

    function initSignalCreateSuccess(successCb?: VoidFunction) {
        return (signal: Signal) => {
            successCb?.();

            showSuccess(signalsI18nKeys.notification.success.addSignal);

            onSignalCreateSuccess?.(signal);
        };
    }

    function initSignalUpdateSuccess(successCb?: VoidFunction) {
        return (signal: Signal) => {
            successCb?.();

            showSuccess(signalsI18nKeys.notification.success.updateSignal);

            onSignalUpdateSuccess?.(signal);
        };
    }

    const CreateView = (props: CreateViewProps) => {
        return (
            <SignalsCreateViewContainer
                viewModel={props.viewModel}
                onCreateSuccess={initSignalCreateSuccess(props.onSubmitSuccess)}
            />
        );
    };

    const EditView = (props: EditViewProps) => {
        return (
            <SignalsUpdateViewContainer
                uiSignal={props.uiSignal}
                onUpdateSuccess={initSignalUpdateSuccess(props.onSubmitSuccess)}
            />
        );
    };

    return (
        <SignalsManagementContainer
            {...signalsManagementProps}
            createView={CreateView}
            editView={EditView}
            onSignalDeleteSuccess={handleSignalDeleteSuccess}
        />
    );
};

export default BaseSignalsManagementContainer;
